import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack, Group, Card, Table, TextInput, Button, ActionIcon, Badge, Text, Chip,
  ScrollArea, Center, Loader, Alert, Box, Tooltip, Checkbox, Select, UnstyledButton,
  SegmentedControl,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  Search, Plus, Pencil, Trash2, TriangleAlert, Users, Copy, X,
  ChevronUp, ChevronDown, ChevronsUpDown, Rows3, Columns3,
} from 'lucide-react';

import { membersApi, groupsApi, epochsApi, type Member } from '../api';
import { GroupTag } from '../components/GroupTag';
import { MemberFormModal } from '../components/MemberFormModal';
import { CopyMembersModal } from '../components/CopyMembersModal';
import { MembersBoard } from '../components/MembersBoard';
import { numStyle } from '../theme';
import { plural } from '../utils/format';

type SortKey = 'number' | 'name' | 'group';
type SortDir = 'asc' | 'desc';

const SORT_OPTIONS = [
  { value: 'number:asc', label: 'Číslo (vzestupně)' },
  { value: 'number:desc', label: 'Číslo (klesajícně)' },
  { value: 'name:asc', label: 'Jméno (A–Z)' },
  { value: 'name:desc', label: 'Jméno (Z–A)' },
  { value: 'group:asc', label: 'Skupina (A–Z)' },
  { value: 'group:desc', label: 'Skupina (Z–A)' },
];

export default function MembersPage() {
  const qc = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 48em)');

  const [q, setQ] = useState('');
  // Ze skupin se sem dá prokliknout na "?skupina=<id>" — filtr pak rovnou sedne.
  const [searchParams, setSearchParams] = useSearchParams();
  const [groupFilter, setGroupFilterState] = useState<string>(searchParams.get('skupina') ?? 'all');

  const setGroupFilter = (value: string) => {
    setGroupFilterState(value);
    // Adresa nemá zůstat viset na skupině, ze které se filtr mezitím přepnul.
    if (value === 'all') searchParams.delete('skupina');
    else if (value === 'nonumber') searchParams.set('skupina', 'nonumber');
    else searchParams.set('skupina', value);
    setSearchParams(searchParams, { replace: true });
  };
  const [sortKey, setSortKey] = useState<SortKey>('number');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const [editing, setEditing] = useState<Member | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  /** Seznam vs. tabule. Volba přežije reload, ať se k ní nemusíš proklikávat. */
  const [view, setView] = useState<'list' | 'board'>(() => {
    try {
      return localStorage.getItem('membersView') === 'board' ? 'board' : 'list';
    } catch {
      return 'list';
    }
  });

  /** Režim hromadného mazání — jinak jsou zaškrtávátka schovaná. */
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const members = useQuery({ queryKey: ['members'], queryFn: () => membersApi.getAll() });
  const groups = useQuery({ queryKey: ['groups'], queryFn: groupsApi.getAll });
  const epochs = useQuery({ queryKey: ['epochs'], queryFn: epochsApi.getAll });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['members'] });
    qc.invalidateQueries({ queryKey: ['groups'] });
  };

  const save = useMutation({
    mutationFn: (data: { number: number | null; name: string; group_id: number | null }) => {
      if (!editing) return membersApi.create(data);
      // updateMemberSchema na serveru bere group_id jen jako kladné číslo, ne null;
      // když skupina není vybraná, pole se prostě nepošle a zůstane, jak bylo.
      const { group_id, ...rest } = data;
      return membersApi.update(editing.id, group_id == null ? rest : { ...rest, group_id });
    },
    onSuccess: () => {
      notifications.show({ message: editing ? 'Změny uloženy' : 'Člen přidán', color: 'forest' });
      setModalOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e: any) =>
      notifications.show({
        title: 'Uložení se nepovedlo',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  /**
   * Přetažení karty na tabuli. Optimisticky překreslíme hned, aby tažení nepůsobilo
   * lagově; při chybě vrátíme cache do původního stavu a řekneme to.
   */
  const move = useMutation({
    mutationFn: ({ member, groupId }: { member: Member; groupId: number | null }) =>
      membersApi.update(member.id, { group_id: groupId }),
    onMutate: async ({ member, groupId }) => {
      await qc.cancelQueries({ queryKey: ['members'] });
      const prev = qc.getQueryData<Member[]>(['members']);
      const target = (groups.data ?? []).find((g) => g.id === groupId);
      qc.setQueryData<Member[]>(['members'], (old) =>
        (old ?? []).map((m) =>
          m.id === member.id
            ? {
                ...m,
                group_id: groupId,
                group_name: target?.name ?? null,
                group_color: target?.color ?? null,
              }
            : m,
        ),
      );
      return { prev };
    },
    onError: (e: any, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['members'], ctx.prev);
      notifications.show({
        title: 'Přesun se nepovedl',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      });
    },
    onSettled: invalidate,
  });

  const removeOne = useMutation({
    mutationFn: membersApi.delete,
    onSuccess: () => { notifications.show({ message: 'Člen smazán' }); invalidate(); },
    onError: (e: any) =>
      notifications.show({
        title: 'Smazání se nepovedlo',
        message: e?.response?.data?.error || e.message,
        color: 'red',
      }),
  });

  const hasNoNumber = (m: Member) => m.number === null || m.number === undefined;
  const withoutNumber = (members.data ?? []).filter(hasNoNumber).length;

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = (members.data ?? []).filter((m) => {
      // 'nonumber' není skupina, ale stav — členy bez čísla mobilní aplikace nezobrazí.
      if (groupFilter === 'nonumber') {
        if (!hasNoNumber(m)) return false;
      } else if (groupFilter !== 'all' && String(m.group_id) !== groupFilter) {
        return false;
      }
      if (!needle) return true;
      return m.name.toLowerCase().includes(needle) || String(m.number ?? '').includes(needle);
    });

    const cs = (a: string, b: string) => a.localeCompare(b, 'cs');
    const dir = sortDir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      if (sortKey === 'number') {
        // Členové bez čísla patří na konec v obou směrech — jinak by při klesajícím
        // řazení zabrali celý začátek tabulky.
        if (hasNoNumber(a) !== hasNoNumber(b)) return hasNoNumber(a) ? 1 : -1;
        if (hasNoNumber(a)) return cs(a.name, b.name);
        return ((a.number as number) - (b.number as number)) * dir;
      }
      if (sortKey === 'group') {
        const ga = a.group_name ?? '';
        const gb = b.group_name ?? '';
        if (ga !== gb) return cs(ga, gb) * dir;
        return cs(a.name, b.name);
      }
      return cs(a.name, b.name) * dir;
    });
  }, [members.data, q, groupFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ChevronsUpDown size={12} opacity={0.4} />;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  const SortHeader = ({ label, keyName }: { label: string; keyName: SortKey }) => (
    <UnstyledButton onClick={() => toggleSort(keyName)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Text component="span" inherit>{label}</Text>
      {sortIcon(keyName)}
    </UnstyledButton>
  );

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (m: Member) => { setEditing(m); setModalOpen(true); };

  const startPicking = () => { setPicking(true); setPicked(new Set()); };
  const stopPicking = () => { setPicking(false); setPicked(new Set()); };

  const togglePick = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allVisiblePicked = visible.length > 0 && visible.every((m) => picked.has(m.id));

  const deletePicked = async () => {
    const chosen = (members.data ?? []).filter((m) => picked.has(m.id));
    if (chosen.length === 0) return;

    const names = chosen.slice(0, 5).map((m) => m.name).join(', ');
    const more = chosen.length > 5 ? ` a ${chosen.length - 5} dalších` : '';
    if (!window.confirm(
      `Smazat ${plural(chosen.length, 'člena', 'členy', 'členů')}?\n\n${names}${more}\n\nTohle nejde vrátit.`
    )) return;

    setDeleting(true);
    const failed: string[] = [];
    // Postupně — hromadný endpoint neexistuje a nechceme na server pustit desítky
    // requestů naráz.
    for (const m of chosen) {
      try {
        await membersApi.delete(m.id);
      } catch (e: any) {
        failed.push(`${m.name}: ${e?.response?.data?.error || e.message}`);
      }
    }
    setDeleting(false);
    invalidate();

    const ok = chosen.length - failed.length;
    if (ok > 0) {
      notifications.show({ message: `Smazáno ${plural(ok, 'člen', 'členové', 'členů')}` });
    }
    if (failed.length > 0) {
      notifications.show({
        title: `Nesmazáno: ${failed.length}`,
        message: failed.join('\n'),
        color: 'red',
        autoClose: false,
      });
    } else {
      stopPicking();
    }
  };

  if (members.isLoading || groups.isLoading) return <Center h={240}><Loader /></Center>;
  if (members.error) {
    return (
      <Alert color="red" title="Členy se nepodařilo načíst">
        {(members.error as any)?.message} — zkus obnovit stránku.
      </Alert>
    );
  }

  const numberCell = (m: Member) =>
    hasNoNumber(m) ? (
      <Tooltip label="Bez čísla — mobilní aplikace člena nezobrazí">
        <Badge variant="outline" color="gray" style={numStyle}>—</Badge>
      </Tooltip>
    ) : (
      <Badge variant="light" color="gray" style={numStyle}>{m.number}</Badge>
    );

  const rowActions = (m: Member) => (
    <Group gap={2} justify="flex-end" wrap="nowrap">
      <ActionIcon aria-label={`Upravit ${m.name}`} onClick={() => openEdit(m)}>
        <Pencil size={14} />
      </ActionIcon>
      <ActionIcon
        aria-label={`Smazat ${m.name}`}
        color="red"
        onClick={() => { if (window.confirm(`Opravdu smazat ${m.name}?`)) removeOne.mutate(m.id); }}
      >
        <Trash2 size={14} />
      </ActionIcon>
    </Group>
  );

  return (
    <Stack gap="sm">
      <Group gap="sm">
        <TextInput
          flex="1 1 190px"
          placeholder="Hledat jméno nebo číslo"
          leftSection={<Search size={14} />}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
        {isMobile && (
          <Select
            w={190}
            aria-label="Řazení"
            data={SORT_OPTIONS}
            value={`${sortKey}:${sortDir}`}
            allowDeselect={false}
            onChange={(v) => {
              if (!v) return;
              const [k, d] = v.split(':');
              setSortKey(k as SortKey);
              setSortDir(d as SortDir);
            }}
          />
        )}
        <SegmentedControl
          aria-label="Zobrazení členů"
          value={view}
          onChange={(v) => {
            const next = v === 'board' ? 'board' : 'list';
            setView(next);
            // Hromadné mazání má zaškrtávátka jen v seznamu.
            if (next === 'board') stopPicking();
            try { localStorage.setItem('membersView', next); } catch { /* soukromé okno */ }
          }}
          data={[
            { value: 'list', label: (<Group gap={6} wrap="nowrap"><Rows3 size={14} /><span>Seznam</span></Group>) },
            { value: 'board', label: (<Group gap={6} wrap="nowrap"><Columns3 size={14} /><span>Tabule</span></Group>) },
          ]}
        />
        <Button leftSection={<Plus size={14} />} onClick={openNew}>Přidat člena</Button>
        <Button variant="default" leftSection={<Copy size={14} />} onClick={() => setCopyOpen(true)}>
          Zkopírovat z epochy
        </Button>
        {view === 'list' && (picking ? (
          <Button variant="default" leftSection={<X size={14} />} onClick={stopPicking}>
            Zrušit mazání
          </Button>
        ) : (
          <Button
            variant="default" color="red" leftSection={<Trash2 size={14} />}
            disabled={(members.data ?? []).length === 0}
            onClick={startPicking}
          >
            Smazat více
          </Button>
        ))}
      </Group>

      <ScrollArea type="never">
        <Chip.Group multiple={false} value={groupFilter} onChange={(v) => setGroupFilter(v as string)}>
          <Group gap={5} wrap="nowrap">
            <Chip value="all" size="xs">Všechny skupiny</Chip>
            <Chip value="nonumber" size="xs" color="orange">
              Bez čísla{withoutNumber > 0 ? ` (${withoutNumber})` : ''}
            </Chip>
            {(groups.data ?? []).map((g) => (
              <Chip key={g.id} value={String(g.id)} size="xs">{g.name}</Chip>
            ))}
          </Group>
        </Chip.Group>
      </ScrollArea>

      {picking && (
        <Alert color="red" variant="light" p="xs">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Group gap="xs">
              <Checkbox
                size="xs"
                checked={allVisiblePicked}
                indeterminate={!allVisiblePicked && visible.some((m) => picked.has(m.id))}
                onChange={(e) =>
                  setPicked(e.currentTarget.checked ? new Set(visible.map((m) => m.id)) : new Set())
                }
                label={`Vybrat vše (${visible.length})`}
              />
              <Text fz="sm" c="dimmed">{plural(picked.size, 'vybraný', 'vybraní', 'vybraných')}</Text>
            </Group>
            <Button
              size="xs" color="red" leftSection={<Trash2 size={14} />}
              loading={deleting} disabled={picked.size === 0} onClick={deletePicked}
            >
              Smazat vybrané ({picked.size})
            </Button>
          </Group>
        </Alert>
      )}

      {view === 'board' ? (
        <MembersBoard
          members={visible}
          groups={groups.data ?? []}
          onEdit={openEdit}
          onMove={(member, groupId) => move.mutate({ member, groupId })}
        />
      ) : (
      <Card padding={0}>
        <Group p="sm" justify="space-between" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Text fw={700} fz="sm">{plural(visible.length, 'člen', 'členové', 'členů')}</Text>
          {withoutNumber > 0 && groupFilter !== 'nonumber' && (
            <Badge
              color="orange" leftSection={<TriangleAlert size={11} />}
              style={{ cursor: 'pointer' }} onClick={() => setGroupFilter('nonumber')}
            >
              {plural(withoutNumber, 'bez čísla', 'bez čísla', 'bez čísla')}
            </Badge>
          )}
        </Group>

        {visible.length === 0 ? (
          <Stack align="center" py="xl" gap={4}>
            <Users size={22} color="var(--mantine-color-dimmed)" />
            <Text fw={600}>Nikoho jsme nenašli</Text>
            <Text c="dimmed" fz="sm">
              {groupFilter === 'nonumber'
                ? 'Všichni členové mají přidělené číslo.'
                : 'Zkus jiné jméno nebo zruš filtr skupiny.'}
            </Text>
          </Stack>
        ) : isMobile ? (
          <Box>
            {visible.map((m) => (
              <Group
                key={m.id} p="sm" gap="sm" wrap="nowrap"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
              >
                {picking && (
                  <Checkbox
                    checked={picked.has(m.id)}
                    onChange={() => togglePick(m.id)}
                    aria-label={`Vybrat ${m.name}`}
                  />
                )}
                {numberCell(m)}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={650} fz="sm" truncate>{m.name}</Text>
                  <GroupTag name={m.group_name} color={m.group_color} />
                </Box>
                {!picking && rowActions(m)}
              </Group>
            ))}
          </Box>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  {picking && (
                    <Table.Th w={40}>
                      <Checkbox
                        size="xs"
                        checked={allVisiblePicked}
                        indeterminate={!allVisiblePicked && visible.some((m) => picked.has(m.id))}
                        onChange={(e) =>
                          setPicked(e.currentTarget.checked ? new Set(visible.map((m) => m.id)) : new Set())
                        }
                        aria-label="Vybrat vše"
                      />
                    </Table.Th>
                  )}
                  <Table.Th w={90}><SortHeader label="Číslo" keyName="number" /></Table.Th>
                  <Table.Th><SortHeader label="Jméno" keyName="name" /></Table.Th>
                  <Table.Th><SortHeader label="Skupina" keyName="group" /></Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map((m) => (
                  <Table.Tr key={m.id} bg={picking && picked.has(m.id) ? 'var(--mantine-color-red-light)' : undefined}>
                    {picking && (
                      <Table.Td>
                        <Checkbox
                          checked={picked.has(m.id)}
                          onChange={() => togglePick(m.id)}
                          aria-label={`Vybrat ${m.name}`}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>{numberCell(m)}</Table.Td>
                    <Table.Td fw={600}>{m.name}</Table.Td>
                    <Table.Td><GroupTag name={m.group_name} color={m.group_color} /></Table.Td>
                    <Table.Td>{!picking && rowActions(m)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
      )}

      <CopyMembersModal
        opened={copyOpen}
        epochs={epochs.data ?? []}
        activeEpochId={epochs.data?.find((e) => e.is_active === 1)?.id ?? null}
        existingNames={(members.data ?? []).map((m) => m.name)}
        onClose={() => setCopyOpen(false)}
      />

      <MemberFormModal
        opened={modalOpen}
        member={editing}
        groups={groups.data ?? []}
        saving={save.isPending}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSubmit={(data) => save.mutate(data)}
      />
    </Stack>
  );
}
